import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FileType } from 'commons/constants/type';
import AssetParamVO from 'commons/vo/param/AssetParamVO';
import QuarterVO from 'commons/vo/QuarterVO';
import {
  requestAsset,
  requestAssetDownload,
  requestAssetDownloadCloudfront,
  requestAssetDownloadS3,
  requestAssetDownloadS3FromWebServer,
  requestAssetList,
  selectQuarter,
  requestInterviewDownloadCloudfront,
  requestHighlightDownloadCloudfront,
} from 'redux/actions/asset/actions';
import { PreviewParamProps } from 'pages/type';
import { HandleDownload } from 'commons/framework/aws/type';
import AssetVO from 'commons/vo/AssetVO';
import WkblVideoVO from 'commons/vo/WkblVideoVO';

type RequestAssetDownloadS3Props = {
  id: number;
};
type RequestAssetDownloadS3FromWebServerProps = {
  id: number;
  title: string;
  filePath: string;
  handleDownload: HandleDownload;
};

export default function useAsset() {
  const dispatch = useDispatch();

  const onRequestAssetList = useCallback(
    (param: AssetParamVO) => {
      dispatch(requestAssetList({ param }));
    },
    [dispatch],
  );

  const onRequestAsset = useCallback(
    (params: PreviewParamProps) => {
      dispatch(requestAsset({ params }));
    },
    [dispatch],
  );

  const onRequestAssetDownload = useCallback(
    ({ videoId, storageId, type }: { videoId: number; storageId: number; type: FileType }) => {
      dispatch(requestAssetDownload({ videoId, storageId, type }));
    },
    [dispatch],
  );

  const onRequestAssetDownloadCloudfront = useCallback(
    ({ categoryId, fileName, downloadFileName }: { categoryId: number; fileName: string; downloadFileName: string; }) => {
      dispatch(requestAssetDownloadCloudfront({ categoryId, fileName, downloadFileName }));
    },
    [dispatch],
  );

  const onRequestInterviewDownloadCloudfront = useCallback(
    ({ downloadUrl }: { downloadUrl: string; }) => {
      dispatch(requestInterviewDownloadCloudfront({ downloadUrl }));
    },
    [dispatch],
  );

  const onRequestHighlightDownloadCloudfront = useCallback(
    ({ downloadUrl }: { downloadUrl: string; }) => {
      dispatch(requestHighlightDownloadCloudfront({ downloadUrl }));
    },
    [dispatch],
  );

  const onRequestAssetDownloadS3 = useCallback(
    ({ id }: RequestAssetDownloadS3Props) => {
      dispatch(requestAssetDownloadS3({ id }));
    },
    [dispatch],
  );

  /**
   * @deprecated
   */
  const onRequestAssetDownloadS3FromWebServer = useCallback(
    ({ id, title, filePath, handleDownload }: RequestAssetDownloadS3FromWebServerProps) => {
      dispatch(requestAssetDownloadS3FromWebServer({ id, title, filePath, handleDownload }));
    },
    [dispatch],
  );

  const onSelectQuarter = useCallback(
    (quarter: QuarterVO | null) => {
      dispatch(selectQuarter({ quarter }));
    },
    [dispatch],
  );

  return useMemo(
    () => ({
      onRequestAssetList,
      onRequestAsset,
      onRequestAssetDownload,
      onRequestAssetDownloadCloudfront,
      onRequestInterviewDownloadCloudfront,
      onRequestHighlightDownloadCloudfront,
      onRequestAssetDownloadS3,
      onSelectQuarter,
      onRequestAssetDownloadS3FromWebServer,
    }),
    [
      onRequestAssetList,
      onRequestAsset,
      onRequestAssetDownload,
      onRequestAssetDownloadCloudfront,
      onRequestInterviewDownloadCloudfront,
      onRequestHighlightDownloadCloudfront,
      onRequestAssetDownloadS3,
      onSelectQuarter,
      onRequestAssetDownloadS3FromWebServer,
    ],
  );
}
